import {
  DioptreError,
  MIRA_CONTEXT_FIELDS,
  RX_READ_COLUMNS,
  RX_READ_EYES,
  RX_READ_TO_GRID,
  dioptre,
  isRxReadAxis,
  type AiTask,
  type RxReadColumn,
  type RxReadEye,
} from '@lensmira/domain'
import { COLUMNS, emptyGrid, entered, type Column, type GridState } from '@lensmira/ui'

/**
 * `AI-001`'s acceptance step, as pure functions. No React in here, so the rule that matters can be
 * tested without a screen.
 *
 * > `AI-001` `MUST`: Mira never writes a prescription value, a price, a stock quantity or a ledger
 * > entry without a human confirming it. A wrong sphere value is a remake; a wrong price is a dispute.
 *
 * **The gate is that {@link composeAnswer} throws.** It cannot return an answer while any field has
 * no decision, or has a decision that does not hold up (an edit that is not a power in 0.25 steps,
 * an accepted value that was never one). The screen disables its button for the same reason, but a
 * disabled button is a courtesy and this is the rule: nothing downstream receives a half-decided
 * proposal, whatever a future caller forgets to check.
 *
 * Decisions are per field, because a proposal is rarely all right or all wrong. A slip read as
 * `+1.75` that the model got right sitting beside a sphere it misread as `-3.75` for `-3.25` is the
 * ordinary case, and a single accept-all is how the second one reaches a lab.
 */

export type Scalar = string | number | boolean

export type PathStep = string | number

export interface ProposalField {
  /** The path joined with dots, `$` for a proposal that is one bare value. Stable per proposal. */
  readonly id: string
  readonly path: readonly PathStep[]
  readonly value: Scalar
}

const MAX_DEPTH = 8

/**
 * Every value the person has to decide about, in the order the proposal holds them.
 *
 * A `null` is not a field. A prescription with no add power has `add: null`, and there is nothing
 * in it to accept or reject; making someone press a button on an absence is noise that trains them
 * to press without looking.
 *
 * **The same argument is applied to a schema's envelope by {@link decisionFields}, which is what
 * this comment used to ask for.** `AI-010` returns `readable`, `script`, `writing`, a `legibility`
 * per eye and `notes` beside the eight prescription values, so a full slip asked for twelve
 * decisions where eight reach a lens. That was latent while no vision task was offered and went
 * live the moment one was. Which leaves are decisions is per task and is data in
 * `MIRA_CONTEXT_FIELDS`; this function still walks every leaf, because the screen shows the context
 * ones and {@link composeAnswer} carries them through untouched.
 */
export function proposalFields(proposed: unknown): readonly ProposalField[] {
  const out: ProposalField[] = []
  const walk = (node: unknown, path: readonly PathStep[], depth: number): void => {
    if (depth > MAX_DEPTH) return
    if (node === null || node === undefined) return
    if (typeof node === 'string' || typeof node === 'number' || typeof node === 'boolean') {
      out.push({ id: path.length === 0 ? '$' : path.join('.'), path, value: node })
      return
    }
    if (Array.isArray(node)) {
      node.forEach((item, index) => {
        walk(item, [...path, index], depth + 1)
      })
      return
    }
    if (typeof node === 'object') {
      for (const [key, value] of Object.entries(node)) walk(value, [...path, key], depth + 1)
    }
  }
  walk(proposed, [], 0)
  return out
}

export type Decision =
  | { readonly kind: 'accept' }
  | { readonly kind: 'edit'; readonly text: string }
  | { readonly kind: 'reject' }

/** A field with no entry is undecided. There is deliberately no "undecided" value to store. */
export type Decisions = Readonly<Record<string, Decision | undefined>>

export type FieldProblem = 'dioptre' | 'axis' | 'number' | 'empty'

export type FieldState =
  | { readonly kind: 'undecided' }
  | { readonly kind: 'rejected' }
  | { readonly kind: 'taken'; readonly value: Scalar }
  | { readonly kind: 'invalid'; readonly problem: FieldProblem }

export type RxColumn = RxReadColumn
export type RxEye = RxReadEye

/**
 * `[od|os, sph|cyl|axis|add]` for a prescription read, or null for anything else.
 *
 * The keys are `@lensmira/domain`'s and not this file's. They used to be written out here as
 * `sphere` and `cylinder`, which is what the capture grid calls its columns and not what the model
 * is asked for: `AI-010`'s schema says `sph` and `cyl`. Nothing matched, so the 0.25 gate below was
 * never reached for a sphere or a cylinder and every axis was rejected as unreadable.
 *
 * `legibility`, which the schema also returns per eye, is deliberately not a position: it is one of
 * three words, not a number, and the generic path decides it.
 */
export function rxPosition(
  task: AiTask,
  path: readonly PathStep[],
): { readonly eye: RxEye; readonly column: RxColumn } | null {
  if (task !== 'rx_ocr' || path.length !== 2) return null
  const [eye, column] = path
  if (!(RX_READ_EYES as readonly unknown[]).includes(eye)) return null
  if (!(RX_READ_COLUMNS as readonly unknown[]).includes(column)) return null
  return { eye: eye as RxEye, column: column as RxColumn }
}

// -----------------------------------------------------------------------------------------------
// AI-001: which leaves are decisions, and which are context
// -----------------------------------------------------------------------------------------------

/** One step of a `MIRA_CONTEXT_FIELDS` pattern against one step of a path. `*` matches any one. */
const stepMatches = (pattern: string, step: PathStep | undefined): boolean =>
  pattern === '*' || (step !== undefined && pattern === String(step))

/**
 * The `MIRA_CONTEXT_FIELDS` pattern this field matches, or null where it is a decision.
 *
 * Returns the pattern rather than a boolean because the screen labels a context field by it: a path
 * of `warnings.3` has no message key of its own, and `warnings.*` does.
 *
 * **A prescription value is checked first and is never context.** The table is data and data gets
 * edited; `od.sph` added to it would change nothing here, so the 0.25 gate and the `AI-001`
 * decision on a power cannot be turned off by a one-line edit somewhere else.
 */
export function contextPatternOf(task: AiTask, path: readonly PathStep[]): string | null {
  if (rxPosition(task, path) !== null) return null
  const fields = MIRA_CONTEXT_FIELDS[task]
  if (fields === undefined) return null
  return (
    fields.paths.find((pattern) => {
      const steps = pattern.split('.')
      return steps.length === path.length && steps.every((step, i) => stepMatches(step, path[i]))
    }) ?? null
  )
}

/**
 * The leaves a person must rule on: everything the task has not declared context.
 *
 * `AI-001` is about values that get written, so this is the list the confirm button waits for and
 * the list {@link composeAnswer} folds into an answer. A task with no entry in the table has every
 * leaf here, which is the fail-closed direction and the one `help`, `report_query` and
 * `message_draft` keep: nothing about them changed.
 */
export function decisionFields(
  task: AiTask,
  fields: readonly ProposalField[],
): readonly ProposalField[] {
  return fields.filter((field) => contextPatternOf(task, field.path) === null)
}

/** The rest: shown, read, carried through an acceptance unchanged, never decided. */
export function contextFields(
  task: AiTask,
  fields: readonly ProposalField[],
): readonly ProposalField[] {
  return fields.filter((field) => contextPatternOf(task, field.path) !== null)
}

/**
 * A prescription value as a number, from the string the model returns or from a typed edit.
 *
 * `AI-010` asks for the value "exactly as written, as a string", so `"-2.25"` and `"+1.00"` are what
 * arrive, signs and all. `Number` reads both. An empty string is not zero, which is why it is
 * refused rather than coerced: `Number('')` is 0, and a blank sphere silently becoming plano is the
 * kind of thing nobody finds until the lens is cut.
 */
export function rxNumber(value: Scalar): number {
  if (typeof value === 'number') return value
  if (typeof value !== 'string' || value.trim() === '') return Number.NaN
  return Number(value.trim())
}

/**
 * Whether a value could be written into a prescription at all.
 *
 * A power must be an exact 0.25 multiple (`dioptre` refuses anything else rather than rounding,
 * because a silently rounded sphere is the most expensive typo in the product), and an axis is a
 * whole number from 1 to 180 (Appendix I). This applies to a value Mira proposed exactly as it
 * applies to one typed in: a model that read `+1.80` has not found a prescription, and accepting it
 * as it stands would put a value in front of the RxCapture grid that the grid then silently drops.
 */
function valueProblem(position: ReturnType<typeof rxPosition>, value: Scalar): FieldProblem | null {
  if (position === null) return null
  const n = rxNumber(value)
  if (isRxReadAxis(position.column)) {
    return Number.isInteger(n) && n >= 1 && n <= 180 ? null : 'axis'
  }
  try {
    dioptre(n)
    return null
  } catch (error) {
    if (error instanceof DioptreError) return 'dioptre'
    throw error
  }
}

/** The value an edit means, typed like the value it replaces, or the reason it is not one. */
function editedValue(
  original: Scalar,
  text: string,
): { readonly value: Scalar } | { readonly problem: FieldProblem } {
  const trimmed = text.trim()
  if (typeof original === 'number') {
    const n = trimmed === '' ? Number.NaN : Number(trimmed)
    return Number.isFinite(n) ? { value: n } : { problem: 'number' }
  }
  if (typeof original === 'boolean') return { problem: 'empty' }
  return trimmed === '' ? { problem: 'empty' } : { value: trimmed }
}

export function fieldState(
  task: AiTask,
  field: ProposalField,
  decision: Decision | undefined,
): FieldState {
  if (decision === undefined) return { kind: 'undecided' }
  if (decision.kind === 'reject') return { kind: 'rejected' }
  const position = rxPosition(task, field.path)
  if (decision.kind === 'accept') {
    const problem = valueProblem(position, field.value)
    return problem === null ? { kind: 'taken', value: field.value } : { kind: 'invalid', problem }
  }
  const edit = editedValue(field.value, decision.text)
  if ('problem' in edit) return { kind: 'invalid', problem: edit.problem }
  const problem = valueProblem(position, edit.value)
  return problem === null ? { kind: 'taken', value: edit.value } : { kind: 'invalid', problem }
}

/**
 * How many fields still stand between the person and the confirm button.
 *
 * Context fields are not counted and are not countable: they have no decision to make, so a panel
 * that counted them would never reach zero. Takes every field and filters, so a caller can pass
 * what it rendered without having to know the split.
 */
export function undecidedCount(
  task: AiTask,
  fields: readonly ProposalField[],
  decisions: Decisions,
): number {
  return decisionFields(task, fields).filter((field) => {
    const state = fieldState(task, field, decisions[field.id])
    return state.kind === 'undecided' || state.kind === 'invalid'
  }).length
}

export class NotDecided extends Error {
  constructor(count: number) {
    super(
      `${count} field(s) still have no valid decision. AI-001 has a person accept, edit or reject every value before anything is recorded.`,
    )
    this.name = 'NotDecided'
  }
}

export interface ComposedAnswer {
  readonly status: 'accepted' | 'edited' | 'rejected'
  /** Absent for a rejection, which takes nothing (`AI_REJECTION_TAKES_NOTHING`). */
  readonly accepted?: unknown
}

/**
 * Every answer {@link composeAnswer} has returned, and nothing else.
 *
 * **Release gate 12, and the reason this is a `WeakSet` rather than a type.** The gate is that
 * "Mira never writes a prescription value without acceptance; attempted programmatically, it is
 * refused" (Appendix U.12). `packages/ui/test/gate-12.gate.test.tsx` holds the half that runs
 * through `CellValue`: a `proposed` cell is not readable as a committed value, carried by a tagged
 * union so there is no `if` to forget.
 *
 * {@link rxGridOf} is a second route to the same grid and it did not exist when that gate was
 * written. It takes a payload and returns `entered` cells, which are the committed kind, so a
 * caller who passed `proposal.proposed` straight in would put Mira's own reading into the capture
 * grid with nothing having been accepted. Nothing stopped that: the parameter was `unknown` and
 * the one real call site passes a {@link composeAnswer} result only by convention.
 *
 * A type brand would be the tidier fix and an `as` cast defeats it. `CLAUDE.md` says a gate must be
 * impossible rather than discouraged, so membership is the test: `composeAnswer` is the only writer,
 * identity is the key, and a hand-built object with the right fields is not in the set. A structured
 * clone of a real answer is a different object and is also refused, so it cannot be smuggled through
 * storage either.
 *
 * It is keyed on the answer object and not on the payload because two answers can hold equal
 * payloads, and because `accepted` is `unknown` and a primitive cannot be a `WeakSet` key.
 */
const DECIDED = new WeakSet<ComposedAnswer>()

/** Attempting the gate 12 bypass: a grid asked for from something no person decided. */
export class NotAccepted extends Error {
  constructor() {
    super(
      'This answer did not come from composeAnswer, so no person accepted the values in it. ' +
        'AI-001 and release gate 12: a prescription value reaches the grid only through the ' +
        'acceptance step.',
    )
    this.name = 'NotAccepted'
  }
}

const clone = (node: unknown): unknown => JSON.parse(JSON.stringify(node)) as unknown

function setAt(root: unknown, path: readonly PathStep[], value: Scalar | null): unknown {
  if (path.length === 0) return value
  let cursor = root as Record<PathStep, unknown>
  for (const step of path.slice(0, -1)) cursor = cursor[step] as Record<PathStep, unknown>
  cursor[path[path.length - 1] as PathStep] = value
  return root
}

/**
 * The person's decisions as the one answer the API takes.
 *
 * The API's answer route takes a status for the whole proposal, so the per-field choices are folded:
 * all rejected is `rejected`; the same shape and values as proposed is `accepted`; anything else is
 * `edited`. A rejected field goes in as `null`, keeping the shape the proposal had, so what was
 * stored beside it (`AI-002`) can be compared field for field.
 *
 * The comparison against the proposal is made here and not left to the server, because the server
 * refuses an `edited` that changed nothing (`AI_EDIT_CHANGED_NOTHING`) and a screen that picks the
 * wrong word would block its own write.
 *
 * **Context fields are carried through exactly as proposed**, not folded and not nulled. Nulling
 * them would make every full acceptance read as `edited`, which is a lie in the one table
 * (`AI-002`) a shop reads its own edit rate out of, and would stop the all-rejected branch below
 * from ever firing on a proposal that has an envelope.
 */
export function composeAnswer(
  task: AiTask,
  proposed: unknown,
  decisions: Decisions,
): ComposedAnswer {
  const fields = decisionFields(task, proposalFields(proposed))
  const outstanding = undecidedCount(task, fields, decisions)
  if (outstanding > 0) throw new NotDecided(outstanding)

  const states = fields.map((field) => fieldState(task, field, decisions[field.id]))
  // A proposal with no fields in it reaches here with nothing decided, because `every` is true of
  // an empty list. `rejected` is the right word for that: there was nothing to accept, and a
  // rejection takes nothing.
  if (states.every((state) => state.kind === 'rejected')) return decided({ status: 'rejected' })

  let accepted = clone(proposed)
  fields.forEach((field, index) => {
    const state = states[index]
    accepted = setAt(accepted, field.path, state?.kind === 'taken' ? state.value : null)
  })
  const unchanged = JSON.stringify(accepted) === JSON.stringify(proposed)
  return decided({ status: unchanged ? 'accepted' : 'edited', accepted })
}

/** The one writer to {@link DECIDED}. Every `return` in {@link composeAnswer} goes through it. */
function decided(answer: ComposedAnswer): ComposedAnswer {
  DECIDED.add(answer)
  return answer
}

/**
 * Accepted prescription values, as cells for the ordinary capture screen.
 *
 * `entered`, not `proposed`: these have each been decided by a person already. They still go through
 * `RxCapture`'s own Save, its own validation and its own idempotency key, so this is a prefill and
 * not a write. A rejected field is `null` in the accepted body and so stays empty.
 *
 * **Takes the whole answer and throws on one it did not issue**, which is release gate 12 and is
 * argued at {@link DECIDED}. It used to take the `accepted` payload as `unknown`, so
 * `rxGridOf(proposal.proposed)` typechecked and would have filled the grid with Mira's own reading.
 */
export function rxGridOf(answer: ComposedAnswer): GridState | null {
  if (!DECIDED.has(answer)) throw new NotAccepted()
  // A rejection has no `accepted` at all (`AI_REJECTION_TAKES_NOTHING`), so this one line covers it
  // and there is no separate check on the status. There was one, and a mutation that broke it left
  // every test green: a rejected answer fell through to here and got the same `null` anyway. Two
  // checks where only one can ever fire is a guard nothing can verify, so the status check is gone
  // and the invariant it was guessing at is asserted where it is true, on `composeAnswer`.
  const accepted = answer.accepted
  if (typeof accepted !== 'object' || accepted === null) return null
  const grid = emptyGrid()
  let any = false
  for (const eye of RX_READ_EYES) {
    const row = (accepted as Record<string, unknown>)[eye]
    if (typeof row !== 'object' || row === null) continue
    for (const column of RX_READ_COLUMNS) {
      const raw = (row as Record<string, unknown>)[column]
      if (typeof raw !== 'string' && typeof raw !== 'number') continue
      const n = rxNumber(raw)
      if (!Number.isFinite(n)) continue
      // The grid's own column name, from the one mapping (`RX_READ_TO_GRID`). The slip abbreviates
      // and the record spells out, and this file used to assume they were the same word.
      const target: Column | undefined = COLUMNS.find((c) => c === RX_READ_TO_GRID[column])
      if (target === undefined) continue
      // Two decimals for a power, because that is the form the grid and the record hold, and the
      // value reaching here has already been checked against the 0.25 grid.
      grid[eye][target] = entered(isRxReadAxis(column) ? String(n) : n.toFixed(2))
      any = true
    }
  }
  return any ? grid : null
}
